import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { trpcClient, setCurrentOrganisationId } from "./trpc";
import { slugify } from "./slugify";

interface Organisation {
  id: string;
  name: string;
}

interface OrganisationContextValue {
  organisations: Organisation[];
  currentOrganisation: Organisation | null;
  setCurrentOrganisation: (org: Organisation) => void;
  addOrganisation: (org: Organisation) => void;
  loading: boolean;
}

const OrganisationContext = createContext<OrganisationContextValue | null>(null);

function getOrgSlugFromUrl(): string | null {
  const match = window.location.pathname.match(/^\/org\/([^/]+)/);
  return match?.[1] ?? null;
}

export function OrganisationProvider({ children }: { children: ReactNode }) {
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [currentOrganisation, setCurrentOrganisation] = useState<Organisation | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trpcClient.listMyOrganisations
      .query()
      .then((orgs) => {
        setOrganisations(orgs);
        if (orgs.length > 0) {
          const urlSlug = getOrgSlugFromUrl();
          const matchedOrg = urlSlug ? orgs.find((org) => slugify(org.name) === urlSlug) : null;
          setCurrentOrganisation(matchedOrg ?? orgs[0]!);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch organisations:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    setCurrentOrganisationId(currentOrganisation?.id ?? null);
  }, [currentOrganisation]);

  const addOrganisation = (org: Organisation) => {
    setOrganisations((prev) => [...prev, org]);
    setCurrentOrganisation(org);
  };

  return (
    <OrganisationContext.Provider
      value={{
        organisations,
        currentOrganisation,
        setCurrentOrganisation,
        addOrganisation,
        loading,
      }}
    >
      {children}
    </OrganisationContext.Provider>
  );
}

export function useOrganisation(): OrganisationContextValue {
  const ctx = useContext(OrganisationContext);
  if (!ctx) {
    throw new Error("useOrganisation must be used within an OrganisationProvider");
  }
  return ctx;
}
